/**
 * Quick task 261002-dfh — Accept / Decline mutations for a triager proposal.
 *
 * Invalidation mirrors `useSetTaskLabels` (the decision changes the task's
 * labels and, for Accept, its assignee): the board cache key plus the task
 * activity timeline.
 *
 * MKS-278 adds the read behind those buttons (`useTriagerProposalState`) and the
 * admin "Dispatch anyway" (`useDispatchHeldTaskAnyway`). Every mutation also
 * invalidates the state query, so withdrawing or force-dispatching an approval
 * re-renders the section from the server rather than from a stale answer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptTriagerProposal,
  declineTriagerProposal,
  getTriagerProposalState,
  type TriagerProposalResult,
  type TriagerProposalState,
} from '../services/api/triagerProposal';
import { overrideRobotHold, type RobotHoldOverrideResult } from '../services/api/robotHolds';
import { taskActivityQueryKey } from './useTaskActivity';

export interface TriagerProposalInput {
  taskId: number;
  projectId: string;
  action: 'accept' | 'decline';
}

export const triagerProposalStateKey = (taskId: number) =>
  ['triager-proposal-state', taskId] as const;

/**
 * MKS-278 — the approval state of one proposal. Only TriagerProposalSection calls it, and that
 * section mounts only for a reader who can act on the proposal, so the extra request never
 * fires for anybody else.
 */
export function useTriagerProposalState(taskId: number) {
  return useQuery<TriagerProposalState>({
    queryKey: triagerProposalStateKey(taskId),
    queryFn: () => getTriagerProposalState(taskId),
    staleTime: 30 * 1000,
  });
}

function useInvalidateAfterDecision(taskId: number, projectId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['board', projectId, 'internal'] });
    queryClient.invalidateQueries({ queryKey: taskActivityQueryKey(taskId) });
    queryClient.invalidateQueries({ queryKey: triagerProposalStateKey(taskId) });
  };
}

export function useTriagerProposalDecision() {
  const queryClient = useQueryClient();
  return useMutation<TriagerProposalResult, Error, TriagerProposalInput>({
    mutationFn: (input) =>
      input.action === 'accept'
        ? acceptTriagerProposal(input.taskId)
        : declineTriagerProposal(input.taskId),
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: ['board', input.projectId, 'internal'] });
      queryClient.invalidateQueries({ queryKey: taskActivityQueryKey(input.taskId) });
      // MKS-278: a decline withdraws the approval, so the section's own read is stale too.
      queryClient.invalidateQueries({ queryKey: triagerProposalStateKey(input.taskId) });
    },
  });
}

/**
 * MKS-278 — the admin "Dispatch anyway" of the task detail: the SAME audited hold override the
 * Bot Admin held list calls (`POST /api/admin/bot/routing/tasks/:taskId/hold-override`), which
 * stamps the override and releases an approved task at once. Admin-only server side.
 */
export function useDispatchHeldTaskAnyway(taskId: number, projectId: string) {
  const invalidate = useInvalidateAfterDecision(taskId, projectId);
  return useMutation<RobotHoldOverrideResult, Error, void>({
    mutationFn: () => overrideRobotHold(taskId),
    onSettled: invalidate,
  });
}
